<script setup lang="ts">
import * as z from 'zod'
import type { FormSubmitEvent } from '@nuxt/ui'

interface CourseDetails {
  id: string
  title: string
  slug: string
  startdate: string
  enddate: string
  is_confirmed: boolean
  price_chf: string | null
  price_text: string | null
  totalLessons: number | null
  coursetype?: { title?: string | null; slug?: string | null }
}

type MeResponse =
  | { authenticated: false }
  | { authenticated: true; userId: string; email: string; firstname: string | null; hasDossier: boolean }

const route = useRoute()

const courseId = computed(() => {
  const v = route.query.kurs
  return typeof v === 'string' ? v : null
})

const { data: courseData, status: courseStatus } = useAsyncData(
  () => `lehrgang-anmeldung-course-${courseId.value ?? 'none'}`,
  () => courseId.value
    ? $fetch<{ course: CourseDetails | null }>('/api/course', { query: { id: courseId.value } })
    : Promise.resolve({ course: null }),
  { server: false, lazy: true, watch: [courseId], default: () => ({ course: null }), getCachedData: () => undefined }
)

const { data: meData, status: meStatus, refresh: refreshMe } = useAsyncData(
  'lehrgang-anmeldung-me',
  () => $fetch<MeResponse>('/api/lehrgang/me'),
  { server: false, lazy: true, default: () => ({ authenticated: false as const }), getCachedData: () => undefined }
)

const course = computed(() => courseData.value?.course ?? null)
const me = computed(() => meData.value ?? { authenticated: false as const })

const loading = computed(() =>
  courseStatus.value === 'pending' || courseStatus.value === 'idle' ||
  meStatus.value === 'pending' || meStatus.value === 'idle'
)

// Auth mode
const authMode = ref<'login' | 'register' | 'forgot-password'>('login')

// Success state
const success = ref(false)
const apiError = ref<string | null>(null)
const submitting = ref(false)
const forgotSent = ref(false)

// Step
const step = computed(() => {
  if (!courseId.value) return 'no-kurs'
  if (loading.value) return 'loading'
  if (!course.value) return 'not-found'
  if (success.value) return 'success'
  if (!me.value.authenticated) return 'auth'
  return 'confirm'
})

// ── Login ────────────────────────────────────────────────────────────────────
const loginSchema = z.object({
  email: z.email('Ungültige E-Mail-Adresse'),
  password: z.string().min(1, 'Passwort erforderlich')
})
type LoginSchema = z.output<typeof loginSchema>
const loginState = reactive({ email: '', password: '' })

async function onLogin(event: FormSubmitEvent<LoginSchema>) {
  submitting.value = true
  apiError.value = null
  try {
    const result = await $fetch<{ ok: boolean; error?: string }>('/api/lehrgang/login', {
      method: 'POST',
      body: { email: event.data.email, password: event.data.password }
    })
    if (result.ok) {
      await refreshMe()
    } else {
      apiError.value = result.error ?? 'Anmeldung fehlgeschlagen.'
    }
  } catch {
    apiError.value = 'Verbindung fehlgeschlagen.'
  } finally {
    submitting.value = false
  }
}

// ── Register ─────────────────────────────────────────────────────────────────
const registerSchema = z.object({
  email: z.email('Ungültige E-Mail-Adresse'),
  password: z.string().min(8, 'Mindestens 8 Zeichen'),
  passwordConfirmation: z.string().min(1, 'Passwortbestätigung erforderlich')
}).refine(d => d.password === d.passwordConfirmation, {
  message: 'Passwörter stimmen nicht überein',
  path: ['passwordConfirmation']
})
type RegisterSchema = z.output<typeof registerSchema>
const registerState = reactive({ email: '', password: '', passwordConfirmation: '' })

async function onRegister(event: FormSubmitEvent<RegisterSchema>) {
  submitting.value = true
  apiError.value = null
  try {
    const result = await $fetch<{ ok: boolean; error?: string }>('/api/lehrgang/register', {
      method: 'POST',
      body: {
        email: event.data.email,
        password: event.data.password,
        password_confirmation: event.data.passwordConfirmation
      }
    })
    if (result.ok) {
      await refreshMe()
    } else {
      apiError.value = result.error ?? 'Registrierung fehlgeschlagen.'
    }
  } catch {
    apiError.value = 'Verbindung fehlgeschlagen.'
  } finally {
    submitting.value = false
  }
}

// ── Forgot Password ───────────────────────────────────────────────────────────
const forgotSchema = z.object({ email: z.email('Ungültige E-Mail-Adresse') })
type ForgotSchema = z.output<typeof forgotSchema>
const forgotState = reactive({ email: '' })

async function onForgotPassword(event: FormSubmitEvent<ForgotSchema>) {
  submitting.value = true
  apiError.value = null
  try {
    const result = await $fetch<{ ok: boolean; error?: string }>('/api/lehrgang/forgot-password', {
      method: 'POST',
      body: { email: event.data.email }
    })
    if (result.ok) {
      forgotSent.value = true
    } else {
      apiError.value = result.error ?? 'Fehler beim Senden.'
    }
  } catch {
    apiError.value = 'Verbindung fehlgeschlagen.'
  } finally {
    submitting.value = false
  }
}

// ── Confirm Registration ──────────────────────────────────────────────────────
const confirmSchema = z.object({
  agb: z.boolean().refine(v => v === true, { message: 'Bitte akzeptiere die AGB.' })
})
type ConfirmSchema = z.output<typeof confirmSchema>
const confirmState = reactive({ agb: false })

async function onConfirm(event: FormSubmitEvent<ConfirmSchema>) {
  if (!course.value || !event.data.agb) return
  submitting.value = true
  apiError.value = null
  try {
    const result = await $fetch<{ ok: boolean; id?: string; error?: string }>('/api/lehrgang/registration', {
      method: 'POST',
      body: { course_id: Number(course.value.id) }
    })
    if (result.ok) {
      success.value = true
    } else {
      apiError.value = result.error ?? 'Anmeldung fehlgeschlagen.'
    }
  } catch {
    apiError.value = 'Verbindung fehlgeschlagen.'
  } finally {
    submitting.value = false
  }
}

// ── Logout ────────────────────────────────────────────────────────────────────
async function doLogout() {
  await $fetch('/api/lehrgang/logout', { method: 'POST' })
  await refreshMe()
  authMode.value = 'login'
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function switchMode(mode: 'login' | 'register' | 'forgot-password') {
  authMode.value = mode
  apiError.value = null
  forgotSent.value = false
}

function formatDate(value: string): string {
  if (!value) return ''
  return new Date(value).toLocaleDateString('de-CH', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  })
}

const priceDisplay = computed(() => {
  const c = course.value
  if (!c) return null
  if (c.price_text) return c.price_text
  if (c.price_chf) {
    const amount = Number(c.price_chf)
    return Number.isNaN(amount) ? null : `CHF ${amount.toFixed(2)}`
  }
  return null
})

useSeoMeta({
  title: 'Lehrgang Anmeldung – Web Professionals',
  description: 'Melde dich verbindlich für einen Lehrgang bei Web Professionals an.'
})
</script>

<template>
  <UPage>
    <UPageBody>
      <UContainer class="max-w-2xl">
        <div class="py-8 border-b border-default mb-8">
          <h1 class="text-3xl font-bold text-highlighted">
            Anmeldung
          </h1>
          <p class="mt-2 text-muted">
            Melde dich verbindlich für deinen Anlass, Kurs oder Lehrgang an.
          </p>
        </div>

        <!-- No kurs param -->
        <div v-if="step === 'no-kurs'" class="my-6">
          <UAlert
            icon="i-lucide-info"
            color="warning"
            variant="subtle"
            title="Kein Lehrgang ausgewählt"
            description="Bitte wähle zuerst einen Lehrgang aus der Übersicht."
          />
          <UButton class="mt-4" to="/lehrgang" icon="i-lucide-arrow-left" variant="subtle">
            Zur Lehrgangsübersicht
          </UButton>
        </div>

        <!-- Loading -->
        <div v-else-if="step === 'loading'" class="flex justify-center py-16">
          <UIcon name="i-lucide-loader-circle" class="animate-spin text-3xl text-primary" />
        </div>

        <!-- Not found -->
        <div v-else-if="step === 'not-found'" class="my-6">
          <UAlert
            icon="i-lucide-alert-triangle"
            color="error"
            variant="subtle"
            title="Lehrgang nicht gefunden"
            description="Der ausgewählte Lehrgang ist nicht verfügbar oder wurde entfernt."
          />
          <UButton class="mt-4" to="/lehrgang" icon="i-lucide-arrow-left" variant="subtle">
            Zur Lehrgangsübersicht
          </UButton>
        </div>

        <!-- Main flow -->
        <div v-else>

          <!-- Course summary card -->
          <UCard class="mb-8">
            <template #header>
              <div class="flex items-start justify-between gap-2">
                <div>
                  <p v-if="course!.coursetype?.title" class="text-xs text-muted uppercase tracking-wide mb-1">
                    {{ course!.coursetype.title }}
                  </p>
                  <h2 class="text-lg font-semibold leading-tight">
                    {{ course!.title }}
                  </h2>
                </div>
                <UBadge v-if="course!.is_confirmed" color="success" variant="subtle" class="shrink-0">
                  Bestätigt
                </UBadge>
              </div>
            </template>

            <dl class="grid gap-x-6 gap-y-2 grid-cols-[max-content_1fr] text-sm">
              <dt class="font-semibold text-default flex items-center gap-2">
                <UIcon name="i-lucide-calendar" class="shrink-0" /> Start
              </dt>
              <dd class="text-muted">
                {{ formatDate(course!.startdate) }}
              </dd>

              <template v-if="course!.totalLessons">
                <dt class="font-semibold text-default flex items-center gap-2">
                  <UIcon name="i-lucide-book-open" class="shrink-0" /> Lektionen
                </dt>
                <dd class="text-muted">
                  {{ course!.totalLessons }} Lektionen
                </dd>
              </template>

              <template v-if="priceDisplay">
                <dt class="font-semibold text-default flex items-center gap-2">
                  <UIcon name="i-lucide-tag" class="shrink-0" /> Teilnahmegebühr
                </dt>
                <dd class="text-muted">
                  {{ priceDisplay }}
                </dd>
              </template>

              <dt class="font-semibold text-default flex items-center gap-2">
                <UIcon name="i-lucide-video" class="shrink-0" /> Unterricht
              </dt>
              <dd class="text-muted">
                Remote – Online via Videokonferenz
              </dd>
            </dl>
          </UCard>

          <!-- Step: Auth (Login / Register) -->
          <div v-if="step === 'auth'">

            <!-- Forgot password -->
            <div v-if="authMode === 'forgot-password'">
              <h2 class="text-xl font-semibold mb-1">
                Passwort vergessen
              </h2>
              <p class="text-muted text-sm mb-6">
                Wir senden dir einen Link zum Zurücksetzen deines Passworts.
              </p>

              <UAlert
                v-if="forgotSent"
                icon="i-lucide-circle-check"
                color="success"
                variant="subtle"
                title="E-Mail gesendet"
                description="Prüfe dein Postfach – der Link ist 60 Minuten gültig."
                class="mb-4"
              />

              <UForm v-else :schema="forgotSchema" :state="forgotState" class="space-y-4" @submit="onForgotPassword">
                <UFormField label="E-Mail" name="email" required>
                  <UInput v-model="forgotState.email" type="email" class="w-full" autocomplete="email" />
                </UFormField>

                <UAlert v-if="apiError" icon="i-lucide-alert-triangle" color="error" variant="subtle" :title="apiError" />

                <div class="flex items-center gap-3">
                  <UButton type="submit" :loading="submitting" trailing-icon="i-lucide-arrow-right">
                    Link senden
                  </UButton>
                  <UButton variant="ghost" @click="switchMode('login')">
                    Zurück zum Login
                  </UButton>
                </div>
              </UForm>
            </div>

            <!-- Login / Register tabs -->
            <div v-else>
              <div class="flex gap-1 mb-6 border-b border-default">
                <button
                  class="px-4 py-2 text-sm font-medium transition-colors"
                  :class="authMode === 'login'
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-muted hover:text-default'"
                  @click="switchMode('login')"
                >
                  Anmelden
                </button>
                <button
                  class="px-4 py-2 text-sm font-medium transition-colors"
                  :class="authMode === 'register'
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-muted hover:text-default'"
                  @click="switchMode('register')"
                >
                  Neues Konto
                </button>
              </div>

              <!-- Login form -->
              <div v-if="authMode === 'login'">
                <UForm :schema="loginSchema" :state="loginState" class="space-y-4" @submit="onLogin">
                  <UFormField label="E-Mail" name="email" required>
                    <UInput v-model="loginState.email" type="email" class="w-full" autocomplete="email" />
                  </UFormField>
                  <UFormField label="Passwort" name="password" required>
                    <UInput v-model="loginState.password" type="password" class="w-full" autocomplete="current-password" />
                  </UFormField>

                  <UAlert v-if="apiError" icon="i-lucide-alert-triangle" color="error" variant="subtle" :title="apiError" />

                  <div class="flex items-center justify-between gap-3">
                    <UButton type="submit" :loading="submitting" trailing-icon="i-lucide-arrow-right">
                      Anmelden
                    </UButton>
                    <UButton variant="ghost" size="sm" @click="switchMode('forgot-password')">
                      Passwort vergessen?
                    </UButton>
                  </div>
                </UForm>
              </div>

              <!-- Register form -->
              <div v-else>
                <p class="text-sm text-muted mb-4">
                  Erstelle ein Konto, um dich für den Lehrgang anzumelden. Die Verwaltung
                  deines Profils erfolgt danach über
                  <ULink href="https://my.web-professionals.ch" target="_blank" class="text-primary">
                    my.web-professionals.ch
                  </ULink>.
                </p>
                <UForm :schema="registerSchema" :state="registerState" class="space-y-4" @submit="onRegister">
                  <UFormField label="E-Mail" name="email" required>
                    <UInput v-model="registerState.email" type="email" class="w-full" autocomplete="email" />
                  </UFormField>
                  <UFormField label="Passwort" name="password" required hint="Mindestens 8 Zeichen">
                    <UInput v-model="registerState.password" type="password" class="w-full" autocomplete="new-password" />
                  </UFormField>
                  <UFormField label="Passwort bestätigen" name="passwordConfirmation" required>
                    <UInput v-model="registerState.passwordConfirmation" type="password" class="w-full" autocomplete="new-password" />
                  </UFormField>

                  <UAlert v-if="apiError" icon="i-lucide-alert-triangle" color="error" variant="subtle" :title="apiError" />

                  <UButton type="submit" :loading="submitting" trailing-icon="i-lucide-arrow-right">
                    Konto erstellen
                  </UButton>
                </UForm>
              </div>
            </div>
          </div>

          <!-- Step: Confirm -->
          <div v-else-if="step === 'confirm'">
            <div class="flex items-center justify-between mb-6">
              <div>
                <h2 class="text-xl font-semibold">
                  Anmeldung bestätigen
                </h2>
                <p v-if="me.authenticated" class="text-sm text-muted mt-0.5">
                  Angemeldet als {{ me.email }}
                </p>
              </div>
              <UButton variant="ghost" size="sm" icon="i-lucide-log-out" @click="doLogout">
                Abmelden
              </UButton>
            </div>

            <UAlert
              v-if="me.authenticated && !me.hasDossier"
              icon="i-lucide-info"
              color="info"
              variant="subtle"
              title="Profil noch unvollständig"
              class="mb-6"
            >
              <template #description>
                Deine Anmeldung wird registriert. Bitte vervollständige danach dein Profil
                (Name, Adresse) auf
                <ULink href="https://my.web-professionals.ch" target="_blank" class="font-medium underline">
                  my.web-professionals.ch
                </ULink>, damit wir dich korrekt erfassen können.
              </template>
            </UAlert>

            <UForm :schema="confirmSchema" :state="confirmState" class="space-y-6" @submit="onConfirm">
              <UFormField name="agb">
                <UCheckbox v-model="confirmState.agb">
                  <template #label>
                    Ich habe die
                    <ULink to="/agb" target="_blank" class="text-primary">
                      Allgemeinen Geschäftsbedingungen
                    </ULink>
                    gelesen und akzeptiere diese.
                  </template>
                </UCheckbox>
              </UFormField>

              <UAlert v-if="apiError" icon="i-lucide-alert-triangle" color="error" variant="subtle" :title="apiError" />

              <UButton
                type="submit"
                :loading="submitting"
                :disabled="!confirmState.agb"
                size="lg"
                trailing-icon="i-lucide-arrow-right"
              >
                Verbindlich anmelden
              </UButton>
            </UForm>
          </div>

          <!-- Step: Success -->
          <div v-else-if="step === 'success'">
            <UAlert
              icon="i-lucide-circle-check"
              color="success"
              variant="subtle"
              title="Anmeldung erfolgreich!"
              class="mb-6"
            >
              <template #description>
                Wir haben deine Anmeldung erhalten und melden uns in Kürze mit allen Details.
                Du findest deine Anmeldung auch in deinem Konto.
              </template>
            </UAlert>

            <div class="flex flex-col sm:flex-row gap-3">
              <UButton
                href="https://my.web-professionals.ch"
                target="_blank"
                trailing-icon="i-lucide-external-link"
              >
                Mein Konto öffnen
              </UButton>
              <UButton to="/lehrgang" variant="subtle" icon="i-lucide-arrow-left">
                Zur Lehrgangsübersicht
              </UButton>
            </div>
          </div>
        </div>
      </UContainer>
    </UPageBody>
  </UPage>
</template>
